import {Assert, GetPercentFromXToY, Lerp, Timer} from "js-vextensions";

export type SirenOptions_Init = ConstructorParameters<typeof SirenOptions>[0];
export class SirenOptions {
	constructor(data: PartialBy<SirenOptions, "autoEndAfter" | "lowFreq" | "highFreq" | "waveType">) {
		Object.assign(this, data);
	}
	autoEndAfter: number|n; // Duration in seconds (null = infinite / no auto-end)
	lowFreq = 300;  // Low frequency in Hz
	highFreq = 1200; // High frequency in Hz
	waveType: OscillatorType = "triangle"; // Wave type

	cycleTime: number; // Time for one up-down cycle in seconds
	volume: number;   // Volume (0-1)
}

export type CycleSlopeInfo = {type: "up" | "down", value: number};

export class PoliceSiren {
	constructor() {
		this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
	}

	sirenOptions: SirenOptions;
	startTime_real = 0;
	// commented; rather than thinking about the drift of system-clock and/or audio-context clock (and trying to handle their mismatch), instead we just have the siren-audio restarted every few minutes
	// (Note: I'd assumed clock drift was inconsequential, but apparently both system-clock and audio-context-clock can drift by >1s over 8hrs! So realignment is indeed needed, which matches my experience.)
	//startTime_audioContext = 0;
	autoEndTimer: Timer|n;
	isPlaying = false;

	Start(options: SirenOptions_Init): void {
		this.Stop(); // stop any existing siren

		this.sirenOptions = new SirenOptions(options);
		this.startTime_real = Date.now();
		//this.startTime_audioContext = this.audioContext.currentTime;
		this.CreateAudioNodes();
		this.isPlaying = true;

		// stop after duration, if specified
		if (this.sirenOptions.autoEndAfter != null) {
			this.autoEndTimer = new Timer(this.sirenOptions.autoEndAfter * 1000, ()=>{
				this.Stop();
			}, 1).Start();
		}
	}
	Stop(): void {
		if (!this.isPlaying) return;
		this.autoEndTimer?.Stop();
		this.autoEndTimer = null;
		this.ClearAudioNodes();
		this.isPlaying = false;
	}

	// audio nodes
	// ==========

	audioContext: AudioContext;
	oscillator: OscillatorNode | null = null;
	gainNode: GainNode | null = null;
	frequencyGainNode: GainNode | null = null;
	lfoOscillator: OscillatorNode | null = null;

	CreateAudioNodes() {
		this.ClearAudioNodes(); // ensure we have a clean slate before creating new audio nodes

		// Resume audio context if suspended (required for user interaction)
		if (this.audioContext.state === "suspended") {
			this.audioContext.resume();
		}

		const {lowFreq, highFreq, cycleTime, volume, waveType} = this.sirenOptions;

		// Create main oscillator (siren sound)
		this.oscillator = this.audioContext.createOscillator();
		this.oscillator.type = waveType;
		this.oscillator.frequency.setValueAtTime(lowFreq, this.audioContext.currentTime);

		// Create LFO (Low Frequency Oscillator) for frequency modulation
		this.lfoOscillator = this.audioContext.createOscillator();
		this.lfoOscillator.frequency.setValueAtTime(1 / cycleTime, this.audioContext.currentTime);
		this.lfoOscillator.type = "triangle";

		// Create gain node for LFO amplitude
		this.frequencyGainNode = this.audioContext.createGain();
		const frequencyRange = (highFreq - lowFreq) / 2;
		this.frequencyGainNode.gain.setValueAtTime(frequencyRange, this.audioContext.currentTime);

		// Create gain node for volume control
		this.gainNode = this.audioContext.createGain();
		this.gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);

		// Connect the nodes
		// LFO -> Frequency Gain -> Main Oscillator frequency
		this.lfoOscillator.connect(this.frequencyGainNode);
		this.frequencyGainNode.connect(this.oscillator.frequency);

		// Main Oscillator -> Volume Gain -> Speakers
		this.oscillator.connect(this.gainNode);
		this.gainNode.connect(this.audioContext.destination);

		// Set the base frequency (center point of the siren sweep)
		const centerFreq = lowFreq + (highFreq - lowFreq) / 2;
		this.oscillator.frequency.setValueAtTime(centerFreq, this.audioContext.currentTime);

		// Start the oscillators
		this.oscillator.start();
		this.lfoOscillator.start();
	}

	ClearAudioNodes() {
		try {
			this.oscillator?.stop();
			this.lfoOscillator?.stop();
		} catch (error) {
			// Oscillator might already be stopped
		}
		this.oscillator = null;
		this.lfoOscillator = null;
		this.gainNode = null;
		this.frequencyGainNode = null;
	}

	UpdateVolume(volume: number): void {
		if (this.gainNode && this.isPlaying) {
			this.gainNode.gain.setValueAtTime(volume.KeepBetween(0, 1), this.audioContext.currentTime);
		}
	}
	// commented for now; calling this "mutating" UpdateCycleTime() appears to mess up the return-values of GetSlopeAtGivenAudioContextTime() (and thus also GetSlopesOfSamplesMatchingGivenTimes())
	/*UpdateFrequencyRange(lowFreq: number, highFreq: number): void {
		if (this.oscillator && this.frequencyGainNode && this.isPlaying) {
			const centerFreq = lowFreq + (highFreq - lowFreq) / 2;
			const frequencyRange = (highFreq - lowFreq) / 2;
			this.oscillator.frequency.setValueAtTime(centerFreq, this.audioContext.currentTime);
			this.frequencyGainNode.gain.setValueAtTime(frequencyRange, this.audioContext.currentTime);
		}
	}
	UpdateCycleTime(cycleTime: number): void {
		if (this.lfoOscillator && this.isPlaying) {
			this.lfoOscillator.frequency.setValueAtTime(1 / cycleTime, this.audioContext.currentTime);
		}
	}*/

	// siren<>breath desync-calculation helpers
	// ==========

	// desync calc-type: peaks
	/** Both startTime and endTime are inclusive. */
	GetCyclePeaksInRange(startTime: number, endTime: number) {
		if (!this.isPlaying || !this.oscillator || !this.lfoOscillator) return []; // Not playing, return empty array
		return PoliceSiren_GetCyclePeaksInRange(startTime, endTime, this.startTime_real, 1 / this.lfoOscillator.frequency.value);
	}

	// desync calc-type: slopes
	GetSlopesOfSamplesMatchingGivenTimes(times: number[]): CycleSlopeInfo[] {
		if (!this.isPlaying || !this.oscillator || !this.lfoOscillator) return []; // Not playing, return empty array
		/*const now_real = Date.now();
		const now_audioContext = this.audioContext.currentTime;*/
		return times.map(time=>{
			//const time_asAudioContextTime = (time - this.startTime_real) / 1000 + this.startTime_audioContext; // Convert to AudioContext time in seconds
			/*const targetTime_offsetFromNow = (time - now_real) / 1000; // convert milliseconds to seconds
			const targetTime_asContextTime = now_audioContext + targetTime_offsetFromNow;*/
			return this.GetSlopeAtGivenTime(time);
		});
	}
	GetSlopeAtGivenTime(time_real: number): CycleSlopeInfo {
		Assert(this.isPlaying && this.lfoOscillator);
		const timeSinceSirenStart = time_real - this.startTime_real; // Calculate time elapsed since the siren started
		const lfoFrequency = this.lfoOscillator.frequency.value; // Get the current LFO frequency (cycles per second)
		const sirenCycleTime = 1 / lfoFrequency; // Calculate the cycle time in seconds
		return PoliceSiren_GetSlopeAtGivenTime(timeSinceSirenStart, sirenCycleTime);
	}
}

/** Both startTime and endTime are inclusive. */
export function PoliceSiren_GetCyclePeaksInRange(startTime: number, endTime: number, sirenStartTimeInMS: number, sirenCycleTimeInSecs: number) {
	const peaks: Array<{time: number}> = [];
	const cycleTimeMS = sirenCycleTimeInSecs * 1000;
	// the peak is 25% through the siren-oscillator's cycle, so add a 25% offset to the siren-oscillator's start-time, to find the time of the first peak
	const firstPeakTime = sirenStartTimeInMS + (cycleTimeMS * 0.25);

	const peakJumpsToReachFirstInRange = startTime > firstPeakTime ? Math.ceil((startTime - firstPeakTime) / cycleTimeMS) : 0;
	let currentPeakTime = firstPeakTime + (peakJumpsToReachFirstInRange * cycleTimeMS);
	while (currentPeakTime <= endTime) {
		peaks.push({time: currentPeakTime});
		currentPeakTime += cycleTimeMS;
	}
	return peaks;
}

export function PoliceSiren_GetSlopesOfSamplesMatchingGivenTimes(times: number[], sirenStartTimeInMS: number, sirenCycleTimeInSecs: number): CycleSlopeInfo[] {
	return times.map(time=>PoliceSiren_GetSlopeAtGivenTime(time - sirenStartTimeInMS, sirenCycleTimeInSecs));
}
export function PoliceSiren_GetSlopeAtGivenTime(timeSinceSirenStartInMS: number, sirenCycleTimeInSecs: number): CycleSlopeInfo {
	const lfoFrequency = 1 / sirenCycleTimeInSecs; // LFO frequency in Hz (cycles per second)
	const cyclesElapsed = (timeSinceSirenStartInMS / 1000) * lfoFrequency; // Calculate how many complete cycles have elapsed
	const positionInCycle = cyclesElapsed % 1; // Get the fractional part to determine position within the current cycle

	// For a triangle wave:
	// - From 0 to 0.25: wave goes from 50% to 100% (increasing)
	// - From 0.25 to 0.75: wave goes from 100% to 0% (decreasing)
	// - From 0.75 to 1: wave goes from 0% to 50% (increasing)
	// Since we're using this to modulate frequency, positive values = higher pitch
	if (positionInCycle < 0.25) return {type: "up", value: Lerp(.5, 1, GetPercentFromXToY(0, 0.25, positionInCycle))};
	if (positionInCycle < 0.75) return {type: "down", value: Lerp(1, 0, GetPercentFromXToY(0.25, 0.75, positionInCycle))};
	return {type: "up", value: Lerp(0, .5, GetPercentFromXToY(0.75, 1, positionInCycle))};
}